import _get from 'lodash/get';
import configs from './configs';
import RemoteCache from './RemoteCache';
import LocalCache from './LocalCache';
import UserProfileCache from './UserProfileCache';
import InventorySearchCache from './InventorySearchCache';
import InventoryCache from './InventoryCache';
import PersonalizationInventory from './PersonalizationInventory';
import LeadProfileCache from './LeadProfileCache';
import TextAlertsSessionCache from './TextAlertsSessionCache';
import { getBrand } from './brandUtils';

/**
 * Personalization Client class to integrate ATC personalization functionality into ATC applications
 * @module PersonalizationClient
 */
export default class PersonalizationClient {
  /**
   * constructor the constructor for the personalization client
   * @param  {Object} config Personalization client configuration object
   */
  constructor(_temp) {
    let {
      env = 'qa',
      remoteApiPath
    } = _temp === void 0 ? {} : _temp;
    this.options = configs[env] || {};
    if (remoteApiPath) {
      // overwrite remoteApiPath
      this.options.remoteApiPath = remoteApiPath;
    }
    this.isAuthenticated = false;
    this.remoteCache = new RemoteCache(this.options);
    this.localCache = new LocalCache(this.options);
    this.userProfileCache = new UserProfileCache();
    this.inventorySearchCache = InventorySearchCache;
    this.inventoryCache = InventoryCache;
    this.personalizationInventory = new PersonalizationInventory(this.options);
    this.leadProfileCache = LeadProfileCache;
    this.textAlertsSessionCache = TextAlertsSessionCache;
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  getApiContext() {
    return this.isAuthenticated ? this.remoteCache : this.localCache;
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object} PersonalizationClient
   */
  setAuthenticated(value) {
    this.isAuthenticated = value;
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async logOut() {
    this.setAuthenticated(false);
    this.userProfileCache.clearItem();
  }

  /**
   * @alias module:PersonalizationClient
   * @param currentPhoneNumber
   * @param updatedPhoneNumber
   * @returns {Promise<*>}
   */
  async validatePhone(currentPhoneNumber, updatedPhoneNumber) {
    return this.remoteCache.validateProfileForm({
      currentPhoneNumber,
      updatedPhoneNumber
    });
  }

  /**
   * Update Profile
   * @alias module:PersonalizationClient
   * @param  {Object}
   * @return {Object}
   */
  async showUpdateUserProfile(_ref) {
    let {
      containerID,
      onAfterSubmit = () => {},
      checkUnverifiedEmail // TODO: this should be removed when Email Verification experiment ends...
    } = _ref;
    // keep track of phone number and zip code because gigya doesn't tell us
    let currentPhoneNumber = '';
    let currentZipCode = '';
    return this.authentication.showUpdateUserProfile({
      onAfterScreenLoad: gigyaEvent => {
        currentPhoneNumber = _get(gigyaEvent.profile, 'phones[0].number', '');
        currentZipCode = _get(gigyaEvent.profile, 'zip');
      },
      // eslint-disable-next-line consistent-return
      onBeforeValidation: async gigyaEvent => {
        // clear form error message
        const formError = document.querySelector('form#gigya-profile-form.gigya-profile-form .gigya-error-display');
        formError.classList.remove('gigya-error-display-active');
        const response = await this.validateProfile(gigyaEvent, currentPhoneNumber, currentZipCode);
        if (response.success) {
          // replace with formatted number before save
          document.querySelector('form#gigya-profile-form input[name="profile.phones.number"]').value = response.formattedUserPhone || '';
        } else {
          // throw errors so Gigya wouldn't update user profile
          return response.errors;
        }
      },
      onAfterSubmit: async gigyaEvent => {
        if (gigyaEvent.response && gigyaEvent.response.errorCode === 0) {
          const email = _get(gigyaEvent, 'profile.email');
          const unverifiedEmails = _get(gigyaEvent, 'response.emails.unverified', []);
          const hasUnverifiedEmail = checkUnverifiedEmail ? unverifiedEmails.includes(email) : false;
          this.remoteCache.updateUserProfile({
            user: gigyaEvent.response,
            emailChanged: hasUnverifiedEmail
          }).then(response => {
            // display confirmation popup if fetch/update is successful
            if (response.userUpdated) {
              this.authentication.showUpdateUserProfileConfirm({
                hasUnverifiedEmail
              });
              currentPhoneNumber = response.userPhone;
              currentZipCode = _get(gigyaEvent.response, 'profile.zip');
            }
          });

          // update user data
          const firstName = _get(gigyaEvent, 'response.profile.firstName');
          if (firstName) {
            this.userProfileCache.updateItems({
              firstName
            });
          } else {
            this.userProfileCache.removeItems(['firstName']);
          }
          onAfterSubmit({
            ...gigyaEvent,
            userProfile: this.userProfileCache.getItem()
          });
        }
      },
      containerID
    });
  }

  /**
   * Update Profile: Open Change Password screen
   * @alias module:PersonalizationClient
   */
  showChangePassword() {
    this.authentication.showChangePassword();
    return this;
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async convertLocalSaves() {
    const localSaves = this.localCache.getItems();
    const convertResponse = await this.remoteCache.addItems(localSaves);
    if (convertResponse.success) {
      this.localCache.clearItems();
    }
    return convertResponse;
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async getSavedInventoryItems() {
    return this.localCache.getItems();
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async saveInventoryItem(item) {
    return this.getApiContext().addItem(item);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async unsaveInventoryItem(item) {
    return this.getApiContext().removeItem(item);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async updateSavedInventoryItem(data) {
    return this.remoteCache.updateItem(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async hydrateInventoryItems(ids) {
    if (ids === void 0) {
      ids = [];
    }
    return this.remoteCache.hydrateItems(ids);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async saveInventoryItems(ids) {
    if (ids === void 0) {
      ids = [];
    }
    return this.getApiContext().addItems(ids);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async setInventoryItems(ids) {
    if (ids === void 0) {
      ids = [];
    }
    return this.localCache.setItems(ids);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async saveSearchItem(item) {
    return this.remoteCache.addSearchItem(item);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async updateSavedSearchItem(data) {
    return this.remoteCache.updateSearchItem(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async unsaveSearchItem(data) {
    return this.remoteCache.unsaveSearchItem(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async updateEmailSavedSearchItem(data) {
    return this.remoteCache.updateLightUserRegistration(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async updateEmailBestDealsItem(data) {
    return this.remoteCache.updateBestDealsRegistration(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async cancelAlerts(data) {
    return this.remoteCache.cancelAllAlerts(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async cancelSearchAlerts(data) {
    return this.remoteCache.cancelSearchAlerts(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async updateAlerts(data) {
    return this.remoteCache.updateAlerts(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async updateSearchAlerts(data) {
    return this.remoteCache.updateSearchAlerts(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @param brand
   * @return {Object}
   */
  async getUserInfo(brand) {
    if (brand === void 0) {
      brand = getBrand();
    }
    return this.remoteCache.getUserInfo({
      brand
    });
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async getBookmarks() {
    return this.remoteCache.getBookmarks();
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  getUserProfileCache() {
    return this.userProfileCache.getItem();
  }

  /**
   * @alias module:PersonalizationClient
   * @param data
   * @returns {this}
   */
  setUserProfileCache(data) {
    return this.userProfileCache.setItem(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @param data
   * @returns {Object}
   */
  updateUserProfileCache(data) {
    return this.userProfileCache.updateItems(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {boolean}
   */
  isUserProfileCacheReady() {
    return this.userProfileCache.isReady();
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {this}
   */
  clearUserProfileCache() {
    return this.userProfileCache.clearItem();
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  removeUserProfileCacheItems(items) {
    if (items === void 0) {
      items = [];
    }
    return this.userProfileCache.removeItems(items);
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Array}
   */
  refreshInventorySearchCache() {
    return this.inventorySearchCache.refreshItems();
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Array}
   */
  setInventorySearchCache(items) {
    if (items === void 0) {
      items = [];
    }
    return this.inventorySearchCache.setItems(items);
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  addInventorySearch(data) {
    return this.inventorySearchCache.addItem(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  removeInventorySearch(data) {
    return this.inventorySearchCache.removeItem(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  editInventorySearch(data) {
    return this.inventorySearchCache.editItem(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @param criteria
   * @returns {Object}
   */
  async getRecentlySearchedInventory(criteria) {
    return this.personalizationInventory.getRecentlySearchedInventory(criteria);
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Array}
   */
  refreshInventoryCache() {
    return this.inventoryCache.refreshItems();
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Array}
   */
  setInventoryCache(items) {
    if (items === void 0) {
      items = [];
    }
    return this.inventoryCache.setItems(items);
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  addInventory(data) {
    return this.inventoryCache.addItem(data);
  }

  /**
   * @alias module:PersonalizationClient
   * @param criteria
   * @returns {Object}
   */
  async getRecentInventory(criteria) {
    return this.inventoryCache.getRecentInventory(criteria);
  }

  /**
   * @alias module:PersonalizationClient
   * @param criteria
   * @returns {Object}
   */
  async getInventoryCounts(criteria) {
    return this.personalizationInventory.getInventoryCounts(criteria);
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  getLeadProfileCache() {
    return this.leadProfileCache.getItem();
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  setLeadProfileCache(item) {
    if (item === void 0) {
      item = {};
    }
    return this.leadProfileCache.setItem(item);
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  getTextAlertsSessionCache() {
    return this.textAlertsSessionCache.getItem();
  }

  /**
   * @alias module:PersonalizationClient
   * @returns {Object}
   */
  setTextAlertsSessionCache(item) {
    if (item === void 0) {
      item = {};
    }
    return this.textAlertsSessionCache.setItem(item);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async getOwnedVehicles() {
    return this.remoteCache.getOwnedVehicles();
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async removeOwnedVehicle(itemId) {
    return this.remoteCache.removeOwnedVehicle(itemId);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {}
   */
  async updateUserOwnedVehicle(updatedVehicle) {
    return this.remoteCache.updateUserOwnedVehicle(updatedVehicle);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {Object}
   */
  async getUserOwnedVehicles() {
    return this.remoteCache.getUserOwnedVehicles();
  }

  /**
   * @alias module:PersonalizationClient
   * @return {}
   */
  async removeUserOwnedVehicle(itemId) {
    return this.remoteCache.removeUserOwnedVehicle(itemId);
  }

  /**
   * @alias module:PersonalizationClient
   * @return {}
   */
  async getPsxNotificationsCount() {
    return this.remoteCache.getPsxNotificationsCount();
  }
}